import React, { createContext, useCallback, useContext, useState } from 'react';

// Staff chat / comments — global "one modal, opened from anywhere" plumbing.
//
// There is exactly ONE permanent thread per entity (a customer KeyCode or a
// BDM visit_id), so we never "create" or "pick" a thread — we just point the
// single mounted <ChatModal/> at an entity and it loads that thread.
//
//   const { openChat } = useChat();
//   openChat({ entityType: 'customer', entityId: 'AB1234', label: 'ACME Motors' });
//
// `label` is only a fallback for the header until the GET returns the
// authoritative entityLabel.

export const ChatContext = createContext(null);

export const useChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used inside <ChatProvider>');
  return ctx;
};

export const ChatProvider = ({ children }) => {
  const [target, setTarget] = useState(null); // { entityType, entityId, label }
  // Bumped every time a thread closes so unread badges can re-fetch (opening a
  // thread auto-stamps it read server-side, so the count should drop to 0).
  const [readVersion, setReadVersion] = useState(0);

  const openChat = useCallback((t) => {
    if (!t || t.entityType == null || t.entityId == null) return;
    setTarget({ ...t, entityId: String(t.entityId) });
  }, []);

  const closeChat = useCallback(() => {
    setTarget(null);
    setReadVersion(v => v + 1);
  }, []);

  return (
    <ChatContext.Provider value={{ target, openChat, closeChat, readVersion }}>
      {children}
    </ChatContext.Provider>
  );
};
